import type React from "react";
import { useState, useEffect } from "react";
import {
  Box,
  InputBase,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  useTheme,
  Avatar,
} from "@mui/material";
import { Send } from "@mui/icons-material";
import { tokens } from "../../../../../theme";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../components/common/ShowToast";
import CustomTextField from "../../../../../components/common/Textfield";
import { IStreamDetails } from "./types/memorial-types";

interface CommentInputProps {
  socketRef: React.RefObject<WebSocket | null>;
  isMobile: boolean;
  serviceType?: string;
}

export default function CommentInput({
  socketRef,
  isMobile,
  serviceType,
}: CommentInputProps) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [first_name, setFirstName] = useState("");
  const [last_name, setLastName] = useState("");
  const [saveDetails, setSaveDetails] = useState(false);
  const [comment, setComment] = useState("");

  // 🧠 Restore saved user details (if any)
  useEffect(() => {
    const savedEmail = localStorage.getItem("commentEmail");
    const savedFullName = localStorage.getItem("commentFullName");
    const savedFirstName = localStorage.getItem("commentFirstName");
    const savedLastName = localStorage.getItem("commentLastName");

    if (savedEmail) setEmail(savedEmail);
    if (savedFullName) setFullName(savedFullName);
    if (savedFirstName) setFirstName(savedFirstName);
    if (savedLastName) setLastName(savedLastName);
  }, []);

  const handleSendClick = () => {
    setOpen(true);
  };

  const handleSave = () => {
    if (!email || !comment) {
      showErrorToast("Email and message are required!", "comment-input-error");
      return;
    }

    if (saveDetails) {
      localStorage.setItem("commentEmail", email);
      localStorage.setItem("commentFullName", `${first_name} ${last_name}`);
      localStorage.setItem("commentFirstName", first_name);
      localStorage.setItem("commentLastName", last_name);
    }

    const payload = {
      type: "message",
      message: comment,
      first_name,
      last_name,
      sender_full_name: fullName || `${first_name} ${last_name}`,
      sender_email: email,
    };

    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(payload));
      showSuccessToast("Comment sent successfully .. waiting for approval!");
    } else {
      console.error("WebSocket is not open. Message not sent.");
      showErrorToast(
        "Failed to send comment. Please try again.",
        "comment-send-error"
      );
    }

    setComment("");
    setOpen(false);
  };

  const isFuneral = serviceType?.toLowerCase() === "funeral";
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          width: "100%",
          mt: 2,
          ...(isFuneral && {
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            backgroundColor: colors.primary[900],
            borderRadius: "12px",
            border: `1px solid ${colors.primary[700]}`,
            width: { xs: "90%", md: "35%" },
            p: 1.5,
            zIndex: 2000,
            boxShadow: 3,
          }),
        }}
      >
        {/* 🟢 Avatar */}
        <Avatar
          sx={{
            bgcolor: "warning.light",
            color: "#ffffffff",
            width: 40,
            height: 40,
            fontSize: "1rem",
            flexShrink: 0,
          }}
        >
          {first_name && last_name
            ? `${first_name[0]}${last_name[0]}`.toUpperCase()
            : ""}
        </Avatar>

        {/* 💬 Input box */}
        <Box
          sx={{
            backgroundColor: colors.primary[800],
            borderRadius: 3,
            display: "flex",
            alignItems: "center",

            flex: 1,
            px: 1,
          }}
        >
          <InputBase
            placeholder="Write a message here ...."
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            sx={{
              flex: 1,
              color: colors.primary[100],
            }}
          />
        </Box>

        {/* 📤 Send button */}
        <IconButton
          onClick={handleSendClick}
          sx={{
            color: "#fff",
            backgroundColor: "info.main",
            "&:hover": { backgroundColor: "info.light" },
            flexShrink: 0,
          }}
        >
          <Send />
        </IconButton>
      </Box>

      {/* 🪟 Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Enter Your Details</DialogTitle>
        <DialogContent>
          <CustomTextField
            fullWidth
            margin="dense"
            label="First Name"
            value={first_name}
            onChange={(e) => setFirstName(e.target.value)}
          />
          <CustomTextField
            fullWidth
            margin="dense"
            label="Surname"
            value={last_name}
            onChange={(e) => setLastName(e.target.value)}
          />
          <CustomTextField
            fullWidth
            margin="dense"
            label="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            type="email"
            required
          />
          <CustomTextField
            fullWidth
            margin="dense"
            label="Your Comment"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            multiline
            rows={4}
            required
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={saveDetails}
                onChange={(e) => setSaveDetails(e.target.checked)}
              />
            }
            label="Save my details for next time"
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpen(false)}
            sx={{ backgroundColor: colors.primary[800] }}
          >
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained">
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
